import { Clapperboard } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { AHEAD_OPTIONS, setVideoPref, useVideoPrefs } from "@/lib/videoPrefs";
import { FIT_MODES } from "@/lib/videoFit";

const Row = ({ title, hint, children }) => (
  <label className="flex items-center justify-between gap-4 py-3">
    <span className="text-sm">{title}{hint && <span className="block text-xs text-zinc-500">{hint}</span>}</span>
    {children}
  </label>
);
const select = "h-9 rounded-md border border-zinc-300 bg-white px-2 text-sm";

/* Video player defaults (also reachable from the player's Options popover); stored in synced video prefs. */
export const VideoDefaultsSection = () => {
  const p = useVideoPrefs();
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-8" data-testid="video-defaults-section">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-zinc-100 text-zinc-700"><Clapperboard size={18} /></div>
        <div>
          <h2 className="text-lg font-medium">Video player</h2>
          <p className="text-xs text-zinc-500">Defaults for movies and shows. Each show can still remember its own aspect choice.</p>
        </div>
      </div>
      <div className="divide-y divide-zinc-100">
        <Row title="Aspect ratio" hint="How the picture fits the screen when a show has no remembered choice">
          <select value={p.fit || "fit"} onChange={(e) => setVideoPref({ fit: e.target.value })} className={select} data-testid="video-default-fit">
            {FIT_MODES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Row>
        <Row title="Hide baked-in black bars" hint="Detect letterbox bars encoded in the picture and zoom past them"><Switch checked={p.smartCrop !== false} onCheckedChange={(on) => setVideoPref({ smartCrop: on })} data-testid="video-default-smartcrop" /></Row>
        <Row title="Smooth buffer" hint="Fetch ahead so slow connections don't stall"><Switch checked={p.smooth !== false} onCheckedChange={(on) => setVideoPref({ smooth: on })} data-testid="video-default-smooth" /></Row>
        <Row title="Buffer ahead">
          <select value={p.ahead || 60} onChange={(e) => setVideoPref({ ahead: Number(e.target.value) })} className={select} data-testid="video-default-ahead">
            {AHEAD_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Row>
        <Row title="Auto play next" hint="Start the next episode when one ends"><Switch checked={p.autoplay !== false} onCheckedChange={(on) => setVideoPref({ autoplay: on })} data-testid="video-default-autoplay" /></Row>
        <Row title="Theater mode" hint="Hide the menu and music player while watching"><Switch checked={!!p.theater} onCheckedChange={(on) => setVideoPref({ theater: on })} data-testid="video-default-theater" /></Row>
      </div>
    </section>
  );
};
